"use client";

import { useEffect, useState } from "react";
import { updateQuietHours, updateFlowMode, type Settings } from "@/lib/settings-api";

interface Props {
  settings: Settings | null;
  onUpdate: (s: Settings) => void;
}

export default function QuietHoursSection({ settings, onUpdate }: Props) {
  const [start, setStart] = useState(settings?.quiet_hours_start || "22:00");
  const [end, setEnd] = useState(settings?.quiet_hours_end || "07:00");
  const [flowMode, setFlowMode] = useState(Boolean(settings?.flow_mode));
  const [hasLocalQuietHoursEdits, setHasLocalQuietHoursEdits] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const settingsReady = Boolean(settings);

  useEffect(() => {
    if (hasLocalQuietHoursEdits) return;
    setStart(settings?.quiet_hours_start || "22:00");
    setEnd(settings?.quiet_hours_end || "07:00");
  }, [hasLocalQuietHoursEdits, settings?.quiet_hours_end, settings?.quiet_hours_start]);

  useEffect(() => {
    setFlowMode(Boolean(settings?.flow_mode));
  }, [settings?.flow_mode]);

  const handleSaveQuietHours = async () => {
    if (!settingsReady) return;
    setSaveError(null);
    try {
      const updated = await updateQuietHours(start, end);
      setHasLocalQuietHoursEdits(false);
      onUpdate(updated);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Could not save quiet hours yet.");
    }
  };

  const handleToggleFlow = async () => {
    if (!settingsReady) return;
    setSaveError(null);
    const nextFlowMode = !flowMode;
    try {
      const updated = await updateFlowMode(nextFlowMode);
      setFlowMode(Boolean(updated.flow_mode));
      onUpdate(updated);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Could not update Flow Mode yet.");
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm p-4" data-testid="settings-quiet-hours-section">
      <h2 className="text-sm font-semibold text-text-light mb-3">Focus & Quiet Hours</h2>

      {/* Flow Mode Toggle */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs font-medium text-text-light">Flow Mode</p>
          <p className="text-[10px] text-muted">Minimize interruptions when you&apos;re focused</p>
        </div>
        <button
          type="button"
          data-testid="settings-flow-mode-toggle"
          aria-pressed={flowMode}
          disabled={!settingsReady}
          onClick={handleToggleFlow}
          className={`w-11 h-6 rounded-full transition-colors relative ${
            flowMode ? "bg-forest" : "bg-border/50"
          } ${!settingsReady ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <div
            className={`w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 transition-transform ${
              flowMode ? "translate-x-[22px]" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>

      {/* Quiet Hours */}
      <div>
        <p className="text-xs font-medium text-text-light mb-2">Quiet Hours</p>
        <div className="flex items-center gap-2">
          <input
            data-testid="settings-quiet-hours-start"
            type="time"
            disabled={!settingsReady}
            value={start}
            onChange={(e) => {
              setHasLocalQuietHoursEdits(true);
              setStart(e.target.value);
            }}
            className="flex-1 px-3 py-2 rounded-lg border border-border bg-card text-xs text-text
                       focus:outline-none focus:ring-2 focus:ring-forest/30"
          />
          <span className="text-xs text-muted">to</span>
          <input
            data-testid="settings-quiet-hours-end"
            type="time"
            disabled={!settingsReady}
            value={end}
            onChange={(e) => {
              setHasLocalQuietHoursEdits(true);
              setEnd(e.target.value);
            }}
            className="flex-1 px-3 py-2 rounded-lg border border-border bg-card text-xs text-text
                       focus:outline-none focus:ring-2 focus:ring-forest/30"
          />
          <button
            type="button"
            data-testid="settings-quiet-hours-save"
            disabled={!settingsReady}
            onClick={handleSaveQuietHours}
            className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
              settingsReady
                ? "text-forest bg-forest/10 hover:bg-forest/20"
                : "cursor-not-allowed bg-secondary text-muted"
            }`}
          >
            Save
          </button>
        </div>
        {saveError ? <p className="mt-2 text-xs text-earth">{saveError}</p> : null}
      </div>
    </div>
  );
}
